/**
 * SpLoginSelector — pre-launch choice between the user's DEDICATED SERVICE
 * PRINCIPAL Epic account (default) and their PERSONAL Epic login, for
 * mortgagee_update_v1_2. Controlled component: the parent owns the boolean so
 * handleLaunch can inject `use_sp_login`. Mirrors OversightLevelSelector
 * (shared <OptionRow>).
 *
 * Add-in copy; ora mirrors this (ui/CLAUDE.md parity). Visibility is gated by
 * the shared `shouldShowSpToggle` helper.
 *
 * features/3-plans/mortgagee-v1-sp-lock.md.
 */
import { OptionRow } from "@product-app/common/components/OptionRow";

export interface SpLoginSelectorProps {
  /** true = Service Principal (default); false = personal Epic login. */
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  /**
   * true = v2's SHARED pool; false/omitted = v1.2's per-user dedicated SP.
   * Only the copy differs — the payload key (`use_sp_login`) is the same — but
   * calling a pooled run "your assigned automation account" is simply untrue:
   * the identity comes from whichever slot the run leases, and the operator has
   * no assigned one.
   */
  pooled?: boolean;
}

function getOptions(pooled?: boolean): {
  key: "sp" | "personal";
  isSp: boolean;
  label: string;
  description: string;
}[] {
  return [
    {
      key: "sp",
      isSp: true,
      label: pooled ? "Shared Service Principal pool" : "Dedicated Service Principal",
      description: pooled
        ? "run under a service principal leased from the shared pool"
        : "run under your assigned automation account",
    },
    {
      key: "personal",
      isSp: false,
      label: "My personal Epic login",
      description: "sign in with your own Epic credentials",
    },
  ];
}

export function SpLoginSelector({ value, onChange, disabled, pooled }: SpLoginSelectorProps) {
  return (
    <div data-testid="sp-login-selector">
      <p className="mb-1 text-xs font-medium text-primary-gray-light-700">
        Epic sign-in
      </p>
      <fieldset
        className="flex flex-col gap-1.5"
        disabled={disabled}
        aria-label="Epic sign-in"
      >
        {getOptions(pooled).map((opt) => (
          <OptionRow
            key={opt.key}
            name="use_sp_login"
            value={opt.key}
            checked={value === opt.isSp}
            onChange={() => onChange(opt.isSp)}
            disabled={disabled}
            testId={`sp-login-option-${opt.key}`}
            className="text-sm"
          >
            <span className="font-semibold">{opt.label}</span>
            {" — "}
            <span className="text-primary-gray-light-600">{opt.description}</span>
          </OptionRow>
        ))}
      </fieldset>
    </div>
  );
}

export default SpLoginSelector;
